// DownloadService.js
import http from "./Http";

/**
 * Descarga un archivo de sesión (Word, PDF o HTML).
 * Si es HTML, el backend devolverá un ZIP comprimido temporal.
 *
 * @param {string} idSesion - ID de la sesión en MongoDB.
 * @param {string} tipoSesion - Tipo de sesión ("SAE" o "SAC").
 * @param {string} tipoArchivo - Tipo de archivo ("word", "pdf", "html").
 */

// Función para descargar el archivo con el nombre proporcionado
const descargarConNombre = (response, filename) => {
  const blob = new Blob([response.data]);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);

  return { status: "ok", filename };
};

const CONNECTORS = new Set(["Y", "E", "DE", "DEL", "LA", "LAS", "EL", "LOS"]);
const GRADE_WORDS = {
  PRIMERO: "1",
  PRIMER: "1",
  SEGUNDO: "2",
  TERCERO: "3",
  TERCER: "3",
  CUARTO: "4",
  QUINTO: "5",
  SEXTO: "6",
};

const normalizarTexto = (value = "") =>
  String(value)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

const obtenerPrimero = (data = {}, keys = [], fallback = "") => {
  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null && data[key] !== "") {
      return data[key];
    }
  }
  return fallback;
};

const obtenerSiglaArea = (data = {}) => {
  const explicita = normalizarTexto(
    obtenerPrimero(data, ["sigla_area_tematica", "siglaArea", "sigla_areaTematica"])
  ).replace(/[^a-zA-Z]/g, "");

  if (explicita) return explicita.toUpperCase();

  const area = normalizarTexto(obtenerPrimero(data, ["area_tematica", "areaTematica"]));
  const words = area
    .replace(/-/g, " ")
    .split(/\s+/)
    .filter((word) => word && !CONNECTORS.has(word.toUpperCase()));

  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();

  const sigla = words
    .map((word) => word[0])
    .join("");

  return sigla.toUpperCase() || "SA";
};

const obtenerGrado = (value) => {
  const text = normalizarTexto(value).toUpperCase();
  const match = text.match(/\d+/);
  if (match) return match[0];

  for (const [word, number] of Object.entries(GRADE_WORDS)) {
    if (text.includes(word)) return number;
  }

  return "1";
};

const obtenerNivel = (value) => {
  const text = normalizarTexto(value).toLowerCase();
  if (text.startsWith("prim")) return "p";
  if (text.startsWith("sec")) return "s";
  if (text.startsWith("inic")) return "i";
  return text[0] || "s";
};

const pad2 = (value) => {
  const match = String(value || "1").match(/\d+/);
  return String(match ? match[0] : "1").padStart(2, "0");
};

const obtenerAreaVisible = (data = {}) =>
  String(obtenerPrimero(data, ["area_tematica", "areaTematica"], "AREA TEMATICA"))
    .trim()
    .toUpperCase();

const obtenerNivelVisible = (value) => {
  const text = String(value || "Secundaria").trim().toLowerCase();
  return text ? `${text[0].toUpperCase()}${text.slice(1)}` : "Secundaria";
};

const construirNombreDescarga = (data = {}, tipoArchivo) => {
  const ext = tipoArchivo === "html" ? "zip" : tipoArchivo === "word" ? "docx" : tipoArchivo;
  const grado = obtenerGrado(obtenerPrimero(data, ["grado"]));
  const nivel = obtenerPrimero(data, ["nivel"]);
  const sesion = pad2(obtenerPrimero(data, ["nro_sesion", "nroSesion", "numero_sesion"]));

  if (tipoArchivo === "html") {
    const codigo = [
      obtenerSiglaArea(data),
      grado,
      obtenerNivel(nivel),
      `-U${pad2(obtenerPrimero(data, ["nro_unidad", "nroUnidad", "numero_unidad"]))}`,
      `S${sesion}`,
    ].join("");

    return `${codigo} · ${obtenerAreaVisible(data)}. ${grado} ${obtenerNivelVisible(nivel)} - Sesión ${sesion}.${ext}`;
  }

  const base = [
    obtenerSiglaArea(data),
    grado,
    obtenerNivel(nivel),
    `-U${pad2(obtenerPrimero(data, ["nro_unidad", "nroUnidad", "numero_unidad"]))}`,
    `S${sesion}`,
  ].join("");

  return `${base}.${ext}`;
};

const descargarArchivoSesion = async (idSesion, tipoSesion, tipoArchivo, datosSesion = null) => {
  try {
    const response = await http.get(
      `/descargar/sesion-aprendizaje/${idSesion}/${tipoSesion}/${tipoArchivo}`,
      {
        responseType: "blob",
      }
    );

    const ext =
      tipoArchivo === "html"
        ? "zip"
        : tipoArchivo === "word"
          ? "docx"
          : tipoArchivo;

    // Usamos el nombre canónico enviado por el backend en Content-Disposition.
    const contentDisposition = response.headers["content-disposition"];
    let filename;

    if (contentDisposition) {
      // Caso para filenames codificados en UTF-8 (RFC 5987)
      const utf8Match = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
      if (utf8Match) {
        filename = decodeURIComponent(utf8Match[1]);
      } else {
        // Caso clásico con filenames en ASCII (sin codificación)
        const asciiMatch = contentDisposition.match(/filename\s*=\s*"?([^";]+)"?/i);
        if (asciiMatch) {
          filename = asciiMatch[1];
        }
      }
    }

    // Si no conseguimos un nombre de archivo, establecemos uno predeterminado
    if (!filename) {

      filename =
        datosSesion && typeof datosSesion === "object"
          ? construirNombreDescarga(datosSesion, tipoArchivo)
          : `Sesion_de_aprendizaje-${tipoSesion}.${ext}`;
    }

    return descargarConNombre(response, filename);
  } catch (error) {
    console.error(`❌ Error descargando ${tipoArchivo}:`, error);
    throw error;
  }
};

const DownloadService = {
  descargarArchivoSesion,
};

export default DownloadService;
